import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { LocationValue } from '@/components/ui/location-autocomplete';
import { getCityCoordinates } from '@/data/usCitiesWithCoords';

const LOCATION_STORAGE_KEY = 'marketplace_location';

interface StoredLocation extends LocationValue {
  lat?: number;
  lng?: number;
}

/**
 * Hook to get and manage user's location for marketplace filtering.
 * Priority: 1) Profile location (logged in) 2) localStorage (guest)
 */
export function useProfileLocation() {
  const { user } = useAuth();
  const [location, setLocation] = useState<LocationValue | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load location on mount and when user changes
  useEffect(() => {
    const loadLocation = async () => {
      setIsLoading(true);
      
      // Try profile first if logged in
      if (user) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('city, state_code, latitude, longitude')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (!error && data?.city && data?.state_code) {
            const loc: LocationValue = {
              city: data.city,
              stateCode: data.state_code,
              state: data.state_code, // Will be display name
              displayName: `${data.city}, ${data.state_code}`
            };
            setLocation(loc);
            
            // Use stored coords or look up
            if (data.latitude && data.longitude) {
              setCoords({ lat: Number(data.latitude), lng: Number(data.longitude) });
            } else {
              const cityCoords = getCityCoordinates(data.city, data.state_code);
              if (cityCoords) setCoords(cityCoords);
            }
            setIsLoading(false);
            return;
          }
        } catch (err) {
          console.error('Failed to load profile location:', err);
        }
      }
      
      // Fall back to localStorage
      try {
        const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (stored) {
          const parsed: StoredLocation = JSON.parse(stored);
          setLocation(parsed);
          if (parsed.lat && parsed.lng) {
            setCoords({ lat: parsed.lat, lng: parsed.lng });
          } else {
            const cityCoords = getCityCoordinates(parsed.city, parsed.stateCode);
            if (cityCoords) setCoords(cityCoords);
          }
        }
      } catch (err) {
        console.error('Failed to parse stored location:', err);
      }
      
      setIsLoading(false);
    };
    
    loadLocation();
  }, [user]);

  // Save location to profile (if logged in) and localStorage
  const saveLocation = async (newLocation: LocationValue | null) => {
    setLocation(newLocation);
    
    if (!newLocation) {
      localStorage.removeItem(LOCATION_STORAGE_KEY);
      setCoords(null);
      
      if (user) {
        await supabase
          .from('profiles')
          .update({ city: null, state_code: null, latitude: null, longitude: null })
          .eq('user_id', user.id);
      }
      return;
    }
    
    // Get coordinates
    const cityCoords = getCityCoordinates(newLocation.city, newLocation.stateCode);
    setCoords(cityCoords);
    
    // Save to localStorage with coords
    const toStore: StoredLocation = {
      ...newLocation,
      lat: cityCoords?.lat,
      lng: cityCoords?.lng
    };
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(toStore));
    
    // Save to profile if logged in
    if (user) {
      await supabase
        .from('profiles')
        .update({
          city: newLocation.city,
          state_code: newLocation.stateCode,
          latitude: cityCoords?.lat,
          longitude: cityCoords?.lng
        })
        .eq('user_id', user.id);
    }
  };

  return {
    location,
    coords,
    isLoading,
    saveLocation,
    hasLocation: !!location
  };
}
